import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { setPersistence, browserLocalPersistence, browserSessionPersistence, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { firebaseAuth } from '@/lib/firebase';

export default function ConsultancyLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [formState, setFormState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ email: '', password: '', remember: false });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('loading');
    setError('');
    try {
      await setPersistence(firebaseAuth, formData.remember ? browserLocalPersistence : browserSessionPersistence);
      const credential = await signInWithEmailAndPassword(firebaseAuth, formData.email.trim(), formData.password);
      if (!credential.user.emailVerified) {
        await signOut(firebaseAuth);
        throw new Error('EMAIL_NOT_VERIFIED');
      }
      navigate('/dashboard');
    } catch (cause) {
      setError(cause instanceof Error && cause.message === 'EMAIL_NOT_VERIFIED' ? 'Please verify your email before signing in.' : 'Unable to sign in. Check your email and password, then try again.');
      setFormState('error');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Decorative */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-navy-800">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-700 via-navy-800 to-navy-900" />

        {/* Floating particles */}
        <div className="absolute inset-0">
          {Array.from({ length: 18 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-1.5 h-1.5 bg-gold-400/20 rounded-full animate-pulse"
              style={{
                left: `${(i * 37) % 100}%`,
                top: `${(i * 53) % 100}%`,
                animationDelay: `${(i % 5) * 0.6}s`,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 flex flex-col justify-center px-16 w-full">
          <img src="/assets/logo2.png" alt="EzyAbroad" className="h-11 w-auto mb-8" />

          <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
            Welcome back.
            <br />
            Your students are waiting.
          </h2>
          <p className="mt-4 text-white/70 text-base leading-relaxed max-w-md">
            Log in to manage your team, track leads, and help more students reach their dreams.
          </p>

          <div className="mt-10 space-y-4 max-w-md">
            {[
              { icon: 'ri-team-line', label: 'Manage your team & counselors' },
              { icon: 'ri-focus-3-line', label: 'Track leads & follow-ups' },
              { icon: 'ri-file-list-3-line', label: 'Applications & document tracking' },
              { icon: 'ri-passport-line', label: 'AI-powered visa guidance' },
              { icon: 'ri-bar-chart-2-line', label: 'Reports & analytics' },
            ].map((feature) => (
              <div key={feature.label} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-gold-400 flex-shrink-0">
                  <i className={`${feature.icon} text-lg`} />
                </div>
                <span className="text-white/90 text-sm md:text-base">{feature.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 md:px-12 py-12 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img src="/assets/logo2-dark.png" alt="EzyAbroad" className="h-9 w-auto mx-auto" />
            <div className="text-xs text-gray-400 italic mt-1">Consultancy Portal</div>
          </div>

          <div className="lg:block hidden mb-8">
            <div className="text-xs text-gray-400 uppercase tracking-wide">Consultancy Portal</div>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-navy-600">Sign in to your account</h1>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            {(location.state as { registered?: boolean } | null)?.registered && <div role="status" className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">Account created successfully. Verify your email, then sign in.</div>}
            {formState === 'error' && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
            <div>
              <label className="block text-sm font-medium text-navy-600 mb-1.5">Email Address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@consultancy.com"
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-navy-600 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter your password"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-colors pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.remember}
                  onChange={(e) => setFormData({ ...formData, remember: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-gold-500 focus:ring-gold-400"
                />
                <span className="text-sm text-gray-500">Remember me for 30 days</span>
              </label>
              <a
                href="/consultancy/forgot-password"
                onClick={(e) => { e.preventDefault(); navigate('/consultancy/forgot-password'); }}
                className="text-sm text-gold-600 hover:text-gold-500 cursor-pointer"
              >
                Forgot your password?
              </a>
            </div>

            <button
              type="submit"
              disabled={formState === 'loading'}
              className="w-full py-3.5 bg-navy-600 text-white font-semibold rounded-lg hover:bg-navy-700 transition-all disabled:opacity-70 flex items-center justify-center gap-2 cursor-pointer"
            >
              {formState === 'loading' ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In to Dashboard'
              )}
            </button>
          </form>

          <div className="mt-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400">OR</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <p className="mt-6 text-center text-sm text-gray-500">
            Don&apos;t have an account?{" "}
            <a
              href="/consultancy/register"
              onClick={(e) => { e.preventDefault(); navigate('/consultancy/register'); }}
              className="text-gold-600 hover:text-gold-500 font-medium cursor-pointer"
            >
              Create one free →
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
